import React, { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Modal, Radio, Tabs, message } from "antd";
import dayjs from "dayjs";
import {
  createBillingPolicyApi,
  updateBillingPolicyApi,
} from "../../../../../services/billingPolicies.services";
import TierEditor from "./TierEditor";
import PreviewPanel from "./PreviewPanel";
import {
  MODE_HELP,
  MODE_LABELS,
  MODE_TABLES,
  STANDARD,
  apiError,
  configurationErrors,
  defaultConfiguration,
} from "./policyUtils";
import styles from "./BillingPolicies.module.css";

const MIN_LEAD_MINUTES = 15;

// mode: "create" | "edit" | "view". "view" is read-only but can still be tested.
const PolicyEditorModal = ({ open, mode, policy, facility, currentPolicy, onClose, onSaved }) => {
  const readOnly = mode === "view";
  const [billingMode, setBillingMode] = useState(STANDARD);
  const [configuration, setConfiguration] = useState({});
  const [effectiveFrom, setEffectiveFrom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    if (policy) {
      setBillingMode(policy.billing_mode);
      setConfiguration(policy.configuration || {});
      setEffectiveFrom(dayjs(policy.effective_from));
    } else {
      setBillingMode(STANDARD);
      setConfiguration({});
      setEffectiveFrom(dayjs().add(1, "day").startOf("day"));
    }
  }, [open, policy]);

  const changeMode = (next) => {
    setBillingMode(next);
    const sameAsSaved = policy?.billing_mode === next;
    setConfiguration(sameAsSaved ? policy.configuration : defaultConfiguration(next));
  };

  const newModeLocked = billingMode !== STANDARD && !facility?.dynamic_billing_enabled;
  const tooSoon =
    effectiveFrom && effectiveFrom.isBefore(dayjs().add(MIN_LEAD_MINUTES, "minute"));
  const canSave =
    !readOnly && effectiveFrom && !tooSoon && !newModeLocked && !configurationErrors(billingMode, configuration);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    const payload = {
      billing_mode: billingMode,
      configuration: billingMode === STANDARD ? {} : configuration,
      effective_from: effectiveFrom.second(0).millisecond(0).format(),
    };
    try {
      if (mode === "edit") {
        await updateBillingPolicyApi(policy.id, payload);
        message.success(`Version ${policy.version} updated`);
      } else {
        await createBillingPolicyApi(facility.id, payload);
        message.success("New billing policy scheduled");
      }
      onSaved();
    } catch (e) {
      setSaveError(apiError(e, "Couldn't save the billing policy"));
    } finally {
      setSaving(false);
    }
  };

  const tables = MODE_TABLES[billingMode] || [];
  const title =
    mode === "create"
      ? `Schedule a new billing policy · ${facility?.name || ""}`
      : `Version ${policy?.version} · ${MODE_LABELS[policy?.billing_mode] || ""}${readOnly ? "" : " (edit)"}`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={1180}
      centered
      destroyOnClose
      footer={
        readOnly
          ? [<Button key="close" onClick={onClose}>Close</Button>]
          : [
              <Button key="cancel" onClick={onClose}>Cancel</Button>,
              <Button key="save" type="primary" loading={saving} disabled={!canSave} onClick={save}>
                {mode === "edit" ? "Save changes" : "Schedule policy"}
              </Button>,
            ]
      }
    >
      <div className={styles.editorGrid}>
        <div className={styles.editorForm}>
          <div className={styles.sectionTitle}>Billing mode</div>
          <Radio.Group
            value={billingMode}
            disabled={readOnly}
            onChange={(e) => changeMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={Object.entries(MODE_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <div className={styles.hint} style={{ marginTop: 8 }}>{MODE_HELP[billingMode]}</div>

          {newModeLocked && !readOnly && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="This mode isn't enabled for this facility yet"
              description="You can configure and test it here. Scheduling it needs the facility to be enabled for dynamic billing."
            />
          )}

          {tables.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className={styles.sectionTitle}>Rules</div>
              {tables.length === 1 ? (
                <>
                  <div className={styles.hint}>{tables[0].help}</div>
                  <TierEditor
                    tiers={configuration[tables[0].key]}
                    readOnly={readOnly}
                    onChange={(tiers) => setConfiguration({ ...configuration, [tables[0].key]: tiers })}
                  />
                </>
              ) : (
                <Tabs
                  size="small"
                  items={tables.map((table) => ({
                    key: table.key,
                    label: table.label,
                    children: (
                      <>
                        <div className={styles.hint}>{table.help}</div>
                        <TierEditor
                          tiers={configuration[table.key]}
                          readOnly={readOnly}
                          onChange={(tiers) => setConfiguration({ ...configuration, [table.key]: tiers })}
                        />
                      </>
                    ),
                  }))}
                />
              )}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <div className={styles.sectionTitle}>Effective from</div>
            <DatePicker
              showTime={{ format: "hh:mm A", minuteStep: 5 }}
              format="DD MMM YYYY, hh:mm A"
              value={effectiveFrom}
              disabled={readOnly}
              status={tooSoon && !readOnly ? "error" : undefined}
              disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
              onChange={setEffectiveFrom}
              allowClear={false}
              style={{ width: 260 }}
            />
            {tooSoon && !readOnly && (
              <div className={styles.tierError}>Schedule at least {MIN_LEAD_MINUTES} minutes ahead</div>
            )}
            <div className={styles.hint} style={{ marginTop: 6 }}>
              Admissions whose first bed is assigned on or after this time will use this policy.
              Existing admissions are not affected.
            </div>
          </div>

          {saveError && <Alert style={{ marginTop: 12 }} type="error" showIcon message={saveError} />}
        </div>

        <PreviewPanel
          facilityId={facility?.id}
          billingMode={billingMode}
          configuration={billingMode === STANDARD ? {} : configuration}
          compareWith={currentPolicy}
        />
      </div>
    </Modal>
  );
};

export default PolicyEditorModal;
